<template>
  <div class="custom-settings-container">
    <!-- Email Settings Section -->
    <div class="settings-section">
      <div class="section-header">
        <div class="section-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        </div>
        <h2>Email Beállítások</h2>
      </div>
      
      <div class="section-content">
        <!-- Email Enable Section -->
        <div class="setting-group">
          <div class="setting-header">
            <h3>Automata email küldés</h3>
          </div>
          <div class="setting-item">
            <div class="checkbox-container">
              <input 
                type="checkbox" 
                v-model="settings.custom_settings.email_enable.pass_req" 
                id="passReq"
                class="modern-checkbox">
              <label for="passReq" class="checkbox-label">
                <span class="checkbox-custom"></span>
                <span class="checkbox-text">Alkalom / bérlet igény</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Email Timing Section -->
        <div class="setting-group">
          <div class="setting-header">
            <h3>Email kiküldés időzítése</h3>
            <p class="setting-description">A megadott nappal és órával az alkalom előtt kerülnek kiküldésre az emailek!</p>
          </div>
          
          <div class="timing-section">
            <h4>Következő alkalom</h4>
            <div class="input-row">
              <div class="input-group">
                <label class="input-label">Nap</label>
                <input 
                  type="number" 
                  class="modern-input" 
                  v-model="settings.custom_settings.email_notifications.next_event.day_before"
                  min="0">
              </div>
              <div class="input-group">
                <label class="input-label">Óra</label>
                <input 
                  type="number" 
                  class="modern-input" 
                  v-model="settings.custom_settings.email_notifications.next_event.hour_before"
                  min="0" max="23">
              </div>
            </div>
          </div>

          <div class="setting-item">
            <label class="input-label">Táncosok száma, ami alatt nem éri meg megtartani a táncórát</label>
            <input 
              type="number" 
              class="modern-input" 
              v-model="settings.custom_settings.child_limit_to_off_event"
              min="0">
          </div>
        </div>

        <!-- Partial Payment Notification Settings -->
        <div class="setting-group">
          <div class="setting-header">
            <h3>Részleges fizetés értesítések</h3>
            <p class="setting-description">Értesítések küldése tanárok és adminisztrátorok részére részleges fizetéssel rendelkező táncosokról.</p>
          </div>

          <div class="checkbox-container">
            <input
              type="checkbox"
              v-model="settings.custom_settings.email_notifications.partial_payment_notification.enabled"
              id="partialPaymentNotification"
              class="modern-checkbox">
            <label for="partialPaymentNotification" class="checkbox-label">
              <span class="checkbox-custom"></span>
              <span class="checkbox-text">Részleges fizetés értesítések engedélyezése</span>
            </label>
          </div>

          <div class="timing-section">
            <h4>Értesítés időzítése</h4>
            <div class="input-row">
              <div class="input-group">
                <label class="input-label">Nap</label>
                <input
                  type="number"
                  class="modern-input"
                  v-model="settings.custom_settings.email_notifications.partial_payment_notification.day_before"
                  min="0">
              </div>
              <div class="input-group">
                <label class="input-label">Óra</label>
                <input
                  type="number"
                  class="modern-input"
                  v-model="settings.custom_settings.email_notifications.partial_payment_notification.hour_before"
                  min="0" max="23">
              </div>
            </div>
          </div>

          <div class="checkbox-container">
            <input
              type="checkbox"
              v-model="settings.custom_settings.email_notifications.partial_payment_notification.include_fully_paid"
              id="includeFullyPaid"
              class="modern-checkbox">
            <label for="includeFullyPaid" class="checkbox-label">
              <span class="checkbox-custom"></span>
              <span class="checkbox-text">Teljesen fizetett táncosokat is értesíteni</span>
            </label>
          </div>
        </div>

        <div class="action-buttons">
          <button class="btn-primary" @click="saveSettings" :disabled="saving">
            <svg v-if="saving" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinning">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17,21 17,13 7,13 7,21"/>
              <polyline points="7,3 7,8 15,8"/>
            </svg>
            {{ saving ? 'Mentés...' : 'Mentés' }}
          </button>
        </div>

        <!-- Status Messages -->
        <div v-if="saved === true" class="status-message success">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22,4 12,14.01 9,11.01"/>
          </svg>
          Sikeresen mentve!
        </div>
        <div v-if="saved === false" class="status-message error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          Valami hiba történt.
        </div>
      </div>
    </div>

    <!-- Season Management Section -->
    <div class="settings-section">
      <div class="section-header">
        <div class="section-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <h2>Szezon Kezelés</h2>
      </div>
      
      <div class="section-content">
        <div class="setting-group">
          <div class="setting-header">
            <h3>Aktuális szezon lezárása</h3>
            <p class="setting-description">A szezon lezárása után új regisztrációk nem lesznek elfogadva.</p>
          </div>
          
          <div class="setting-item">
            <label class="input-label">Szezon formátum</label>
            <input 
              type="text" 
              class="modern-input" 
              v-model="seasonEndFormat" 
              placeholder="Akármilyen formátum megadható, de javasolt a: '2024/2025' forma használata">
          </div>

          <div class="action-buttons">
            <button class="btn-danger" @click="endSeason" :disabled="endingSeason">
              <svg v-if="endingSeason" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinning">
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
              {{ endingSeason ? 'Lezárás...' : 'Szezon lezárása' }}
            </button>
          </div>

          <!-- Season End Status -->
          <div v-if="seasonEndMsg === true" class="status-message success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            Szezon sikeresen lezárva!
          </div>
          <div v-if="seasonEndMsg === false" class="status-message warning">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Szezon lezárva.
          </div>
        </div>
      </div>
    </div>

    <!-- Monthly Payment Visual Reset Section -->
    <div class="settings-section">
      <div class="section-header">
        <div class="section-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
          </svg>
        </div>
        <h2>Havi Befizetés Vizuális Nullázása (Számlázás)</h2>
      </div>

      <div class="section-content">
        <div class="setting-group">
          <div class="setting-header">
            <h3>Havi fizetések automatikus nullázása</h3>
            <p class="setting-description">
              A hónap végén (vagy a megadott napon és időpontban) a táncosok havi befizetett összege vizuálisan nullázódik a Számlázás felületen.
              Így az adminisztrátorok azonnal tisztán látják az új hónap indulásakor, ki fizetett már az adott hónapra és ki nem.
            </p>
          </div>

          <div class="setting-item">
            <div class="checkbox-container">
              <input 
                type="checkbox" 
                v-model="monthlyResetConfig.enabled" 
                id="resetEnabled"
                class="modern-checkbox">
              <label for="resetEnabled" class="checkbox-label">
                <span class="checkbox-custom"></span>
                <span class="checkbox-text">Automatikus havi nullázás engedélyezése</span>
              </label>
            </div>
          </div>

          <div class="timing-section" v-if="monthlyResetConfig.enabled">
            <h4>Nullázás időzítése</h4>
            <div class="input-row">
              <div class="input-group">
                <label class="input-label">Nullázás napja</label>
                <select v-model="monthlyResetConfig.reset_day" class="modern-input">
                  <option value="last_day">Hónap utolsó napja (éjjel - alapértelmezett)</option>
                  <option value="1">Hónap 1. napja</option>
                  <option value="5">Hónap 5. napja</option>
                  <option value="10">Hónap 10. napja</option>
                  <option value="15">Hónap 15. napja</option>
                  <option value="20">Hónap 20. napja</option>
                  <option value="25">Hónap 25. napja</option>
                  <option value="28">Hónap 28. napja</option>
                </select>
              </div>
              <div class="input-group">
                <label class="input-label">Időpont (óra)</label>
                <select v-model.number="monthlyResetConfig.reset_hour" class="modern-input">
                  <option :value="23">Éjjel 23:00 (alapértelmezett)</option>
                  <option :value="0">Éjfél (00:00)</option>
                  <option :value="20">Este 20:00</option>
                  <option :value="21">Este 21:00</option>
                  <option :value="22">Este 22:00</option>
                  <option v-for="h in 24" :key="'reset-hour-' + (h - 1)" :value="h - 1">
                    {{ String(h - 1).padStart(2, '0') }}:00
                  </option>
                </select>
              </div>
            </div>

            <div class="info-notice-box mt-3">
              <div class="notice-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-1.5 text-primary">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                Fontos működési garanciák:
              </div>
              <ul class="notice-list mb-0">
                <li><strong>A korábbi tartozások NEM nullázódnak:</strong> Ha a táncosnak elmaradása vagy tartozása volt, az automatikusan átvitelre kerül a következő hónapra, és fizetendő marad.</li>
                <li><strong>Minden adat megmarad a statisztikákban:</strong> A korábbi tranzakciók, előzmények és bevételek változatlanul megőrződnek a Statisztikák és Kimutatások menüpontban.</li>
                <li><strong>Vizuális tiszta lap:</strong> Az új hónapban a havi befizetés 0 Ft-ról indul, így egyértelmű, hogy az új hónap tagdíja már rendezve van-e.</li>
              </ul>
            </div>
          </div>

          <div class="action-buttons">
            <button class="btn-primary" @click="saveSettings" :disabled="saving">
              <svg v-if="saving" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinning">
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17,21 17,13 7,13 7,21"/>
                <polyline points="7,3 7,8 15,8"/>
              </svg>
              {{ saving ? 'Mentés...' : 'Beállítások Mentése' }}
            </button>

            <button class="btn-warning-action" @click="manualResetNow" :disabled="triggeringReset">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="1 4 1 10 7 10"/>
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
              </svg>
              {{ triggeringReset ? 'Nullázás...' : 'Vizuális nullázás futtatása most' }}
            </button>
          </div>

          <!-- Status Messages -->
          <div v-if="resetSuccessMsg" class="status-message success mt-3">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            {{ resetSuccessMsg }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import axios from 'axios';
  export default {
    data() {
      return {
        settings: {
          custom_settings: {
            email_enable: { pass_req: false },
            email_notifications: {
              next_event: { day_before: 0, HourBefore: 0 },
              event_off_admin: { day_before: 0, HourBefore: 0 },
              partial_payment_notification: {
                enabled: true,
                day_before: 1,
                hour_before: 12,
                include_fully_paid: false
              }
            },
            child_limit_to_off_event: 0,
            monthly_reset: {
              enabled: true,
              reset_day: 'last_day',
              reset_hour: 23,
              reset_minute: 0,
              last_manual_reset: ''
            }
          },
          message: ""
        },
        saved: null,   
        seasonEndFormat: "",
        seasonEndMsg: null,
        resetSuccessMsg: null,
        saving: false,
        endingSeason: false,
        triggeringReset: false,
      };
    },
    computed: {
      monthlyResetConfig() {
        if (!this.settings || !this.settings.custom_settings) {
          return { enabled: true, reset_day: 'last_day', reset_hour: 23, reset_minute: 0 };
        }
        if (!this.settings.custom_settings.monthly_reset) {
          this.settings.custom_settings.monthly_reset = {
            enabled: true,
            reset_day: 'last_day',
            reset_hour: 23,
            reset_minute: 0,
            last_manual_reset: ''
          };
        }
        return this.settings.custom_settings.monthly_reset;
      }
    },
    created() {
      this.fetchSettings();
    },
    methods: {
      async manualResetNow() {
        const confirmed = confirm('Biztosan futtatod most a havi befizetések vizuális nullázását?\n\nA Számlázásban az új hónap 0 Ft-ról indul, a korábbi tartozások átvitelre kerülnek és a statisztikai adatok megmaradnak.');
        if (!confirmed) return;
        this.triggeringReset = true;
        try {
          const res = await axios.post(this.$BACKEND_HOST + '/api/billing/reset-now');
          this.resetSuccessMsg = 'A havi befizetések sikeresen vizuálisan nullázva lettek a Számlázás felületen!';
          if (this.settings && this.settings.custom_settings && this.settings.custom_settings.monthly_reset) {
            this.settings.custom_settings.monthly_reset.last_manual_reset = res.data.last_manual_reset;
          }
        } catch (error) {
          console.error('Error triggering manual reset:', error);
          alert('Hiba történt a nullázás futtatása során.');
        } finally {
          this.triggeringReset = false;
          setTimeout(() => {
            this.resetSuccessMsg = null;
          }, 4000);
        }
      },
      async fetchSettings() {
        try {
          const response = await axios.get(this.$BACKEND_HOST + '/api/get-cust-settings');
          this.settings = response.data;
          console.log(this.settings);
        } catch (error) {
          console.error('Error fetching settings:', error);
        }
      },
      async saveSettings() {
        this.saving = true;
        try {
          await axios.post(this.$BACKEND_HOST + '/api/save-cust-settings', this.settings.custom_settings);
          this.saved = true;
        } catch (error) {
          this.saved = false;
          console.error('Error saving settings:', error);
        }
        this.saving = false;
        setTimeout(()=>{ 
          this.saved = null; 
        }, 3000);
      },


      async endSeason() {
        let confirmSeasonEnd = confirm(`Biztosan lezárod a ${this.seasonEndFormat} szezont?`);
        if(confirmSeasonEnd){
          this.endingSeason = true;
          try {
            await axios.post(this.$BACKEND_HOST + '/api/end-current-season', {"season_end":this.seasonEndFormat});
            this.seasonEndMsg = true;
          } catch (error) {
            this.seasonEndMsg = false;
            console.error('Error ending current season settings:', error);
          }
          this.endingSeason = false;
          setTimeout(()=>{ 
            this.seasonEndMsg = null;
          }, 3000);
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .custom-settings-container {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
  }

  .settings-section {
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow);
    border: 1px solid var(--color-border);
    overflow: hidden;
    margin-bottom: 2rem;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 2rem;
    background: var(--color-background-mute);
    border-bottom: 1px solid var(--color-border);
  }

  .section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
    border-radius: 8px;
    color: white;
    flex-shrink: 0;
  }

  .section-header h2 {
    color: var(--color-heading);
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }

  .section-content {
    padding: 2rem;
  }

  .setting-group {
    margin-bottom: 2rem;
  }

  .setting-group:last-child {
    margin-bottom: 0;
  }

  .setting-header {
    margin-bottom: 1.5rem;
  }

  .setting-header h3 {
    color: var(--color-heading);
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }

  .setting-description {
    color: var(--color-text-muted);
    font-size: 0.95rem;
    margin: 0;
    line-height: 1.5;
  }

  .setting-item {
    margin-bottom: 1.5rem;
  }

  .setting-item:last-child {
    margin-bottom: 0;
  }

  .timing-section {
    background: var(--color-background-mute);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid var(--color-border);
  }

  .timing-section h4 {
    color: var(--color-heading);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
  }

  .input-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .input-label {
    color: var(--color-heading);
    font-size: 0.95rem;
    font-weight: 500;
    margin: 0;
  }

  .modern-input {
    padding: 0.75rem 1rem;
    border: 2px solid var(--color-border);
    border-radius: 8px;
    background: var(--card-bg);
    color: var(--color-text);
    font-size: 0.95rem;
    transition: all 0.3s ease;
    outline: none;
  }

  .modern-input:focus {
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgba(var(--primary-blue-rgb), 0.1);
  }

  .modern-input:hover {
    border-color: var(--primary-blue);
  }

  .checkbox-container {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .modern-checkbox {
    display: none;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    color: var(--color-text);
    font-size: 0.95rem;
    font-weight: 500;
  }

  .checkbox-custom {
    width: 20px;
    height: 20px;
    border: 2px solid var(--color-border);
    border-radius: 4px;
    background: var(--card-bg);
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .modern-checkbox:checked + .checkbox-label .checkbox-custom {
    background: var(--primary-blue);
    border-color: var(--primary-blue);
  }

  .modern-checkbox:checked + .checkbox-label .checkbox-custom::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 6px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .checkbox-text {
    user-select: none;
  }

  .action-buttons {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--color-border);
  }

  .btn-primary, .btn-danger {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    outline: none;
  }

  .btn-primary {
    background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
    color: white;
    box-shadow: var(--shadow-sm);
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .btn-danger {
    background: linear-gradient(135deg, var(--error), #dc3545);
    color: white;
    box-shadow: var(--shadow-sm);
  }

  .btn-danger:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .btn-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .status-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    margin-top: 1rem;
    border: 1px solid;
  }

  .status-message.success {
    background: rgba(34, 197, 94, 0.1);
    color: #16a34a;
    border-color: rgba(34, 197, 94, 0.2);
  }

  .status-message.error {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
    border-color: rgba(239, 68, 68, 0.2);
  }

  .status-message.warning {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706;
    border-color: rgba(245, 158, 11, 0.2);
  }

  .btn-warning-action {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: 1px solid var(--color-border);
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    background: var(--color-background-mute);
    color: var(--color-heading);
    box-shadow: var(--shadow-sm);
  }

  .btn-warning-action:hover:not(:disabled) {
    transform: translateY(-2px);
    border-color: var(--warning, #f59e0b);
    color: var(--warning, #d97706);
    box-shadow: var(--shadow-md);
  }

  .btn-warning-action:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .info-notice-box {
    background: rgba(37, 99, 235, 0.05);
    border: 1px solid rgba(37, 99, 235, 0.15);
    border-radius: 10px;
    padding: 1.1rem 1.25rem;
  }

  .notice-title {
    font-weight: 700;
    color: var(--color-heading);
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
  }

  .notice-list {
    color: var(--color-text-muted);
    font-size: 0.88rem;
    line-height: 1.5;
    padding-left: 1.25rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .custom-settings-container {
      padding: 1rem;
    }

    .section-header {
      padding: 1rem 1.5rem;
    }

    .section-header h2 {
      font-size: 1.25rem;
    }

    .section-content {
      padding: 1.5rem;
    }

    .input-row {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .action-buttons {
      flex-direction: column;
    }

    .btn-primary, .btn-danger {
      justify-content: center;
    }
  }

  @media (max-width: 480px) {
    .custom-settings-container {
      padding: 0.75rem;
    }

    .section-header {
      padding: 0.75rem 1rem;
    }

    .section-header h2 {
      font-size: 1.125rem;
    }

    .section-content {
      padding: 1rem;
    }

    .timing-section {
      padding: 1rem;
    }

    .setting-header h3 {
      font-size: 1.125rem;
    }

    .btn-primary, .btn-danger {
      padding: 0.625rem 1.25rem;
      font-size: 0.875rem;
    }
  }
  </style>
  